/**
 *
 * @module dssWebPlayerAdapter
 * @desc The SDK.Media.PlayerAdapter instance for the Disney Streaming web player.
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 * @see https://github.bamtech.co/pages/btm-browser-media-platform/documentation/jsdoc/btm-browser-media-platform-common/MediaPlatform.html
 * @see https://github.bamtech.co/pages/btm-browser-media-platform/documentation/jsdoc/btm-browser-media-platform-common/MediaPlatform.Common.Constants.Events.html
 * @see https://github.bamtech.co/btm-browser-media-platform
 *
 */

import Check from 'check-types';

import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const Util = Services.Util;
const DrmProvider = Drm.DrmProvider;
const DrmType = Services.Media.DrmType;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    assigned,
    function: isFunction,
    instanceStrict,
    nonEmptyObject,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class DssWebPlayerAdapter
 * @extends {SDK.Media.PlayerAdapter}
 * @since 3.9.0
 * @desc Interface used to communicate with the Disney Streaming web player.
 * @note Disney Streaming web player.
 *
 */
export default class DssWebPlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {NativePlayer} nativePlayer - An instance of the BAMTECH web player.
     * @throws {InvalidArgumentException}
     *
     */
    constructor(nativePlayer) {

        const errorMessage = {
            ctor: 'DssWebPlayerAdapter(nativePlayer)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            }
        };

        assigned(nativePlayer, errorMessage.nativePlayer, InvalidArgumentException);

        super(nativePlayer);

        /**
         *
         * @access private
         * @type {Array<SDK.Drm.DrmProvider>}
         * @desc Set of DRM providers
         *
         */
        this.drmProviders = [];

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.trackingData = null;

        /**
         *
         * @desc ensure a proper accessToken is passed on xhr requests
         *
         */
        nativePlayer.drm.setXhrConfigProvider(() => ({
            headers: {
                key: {
                    Authorization: this.accessToken
                },
                adEngine: this.getAdEngineHeaders()
            }
        }));

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the `PlaybackSession`).
     * Sets the source URI on the `NativePlayer` instance.
     * @returns {Promise<Void>} - A promise that resolves after the `playlistUri` has been set.
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: `${this.constructor.name}.setSource(playlistUri)`,
            get playlistUri() {
                return `${this.method} playlistUri should be a String with length`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        return new Promise((resolve) => {

            this.playlistUri = playlistUri;

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @desc Gets a snapshot of information about media playback.
     * @returns {Promise<PlaybackMetrics|ServiceException>} - instance that contains a snapshot
     * of information about media playback.
     * @note metric value is rounded down to prevent possible service issues with floats
     * @note executed by `PlaybackTelemetryDispatcher.recordStreamSample`
     *
     */
    getPlaybackMetrics() {

        const errorMessage = {
            method: `${this.constructor.name}.getPlaybackMetrics()`,
            get currentPlayhead() {
                return `${this.method} unable to get NativePlayer playhead data`;
            },
            get currentBitrate() {
                return `${this.method} unable to get NativePlayer bitrate data`;
            }
        };

        const { nativePlayer, unavailableMetric } = this;

        if (Check.not.assigned(nativePlayer.bitrates)) {

            const reasons = [new ErrorReason('', errorMessage.currentBitrate)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        if (Check.not.assigned(nativePlayer.currentTime)) {

            const reasons = [new ErrorReason('', errorMessage.currentPlayhead)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        const currentBitrate = Math.floor(nativePlayer.bitrates.currentKbps) || unavailableMetric;
        const currentPlayhead = Math.floor(nativePlayer.currentTime) || unavailableMetric;

        return Promise.resolve(new PlaybackMetrics(currentBitrate, currentPlayhead));

    }

    /**
     *
     * @access public
     * @param {PlaybackEventListener} listener - The instance of the `PlaybackEventListener` to use.
     * @returns {Promise<Void|ServiceException>} - A promise that resolves after the listeners have been added.
     * @todo Add Listeners for telemetry
     * @todo need to check new mediaResource
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: `${this.constructor.name}.addListener(listener)`,
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers } = this;
        const { onPlay, onPause, onError, onPlayedToCompletion } = this;
        const { PlayerEvents, ErrorEvents } = nativePlayer.events;
        const { MEDIA_COMPLETED, MEDIA_PAUSED, MEDIA_RESUMED, MEDIA_STARTED } = PlayerEvents.PLAYBACK;
        const { MEDIA_ERROR } = ErrorEvents;

        return new Promise((resolve, reject) => {

            if (isFunction(nativePlayer.on) && isFunction(nativePlayer.off)) {

                this.listener = listener;

                boundHandlers.onPlay = onPlay.bind(this);
                boundHandlers.onPause = onPause.bind(this);
                boundHandlers.onError = onError.bind(this);
                boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);

                nativePlayer.on(MEDIA_STARTED, boundHandlers.onPlay);
                nativePlayer.on(MEDIA_RESUMED, boundHandlers.onPlay);
                nativePlayer.on(MEDIA_PAUSED, boundHandlers.onPause);
                nativePlayer.on(MEDIA_ERROR, boundHandlers.onError);
                nativePlayer.on(MEDIA_COMPLETED, boundHandlers.onPlayedToCompletion);

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.addListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @param {PlaybackEventListener} listener - The instance of the `PlaybackEventListener` to use.
     * @returns {Promise<Void>} - A promise that resolves after the listeners have been removed.
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: `${this.constructor.name}.removeListener(listener)`,
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get removeListener() {
                return `${this.method} unable to remove PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers } = this;
        const { PlayerEvents, ErrorEvents } = nativePlayer.events;
        const { MEDIA_COMPLETED, MEDIA_PAUSED, MEDIA_RESUMED, MEDIA_STARTED } = PlayerEvents.PLAYBACK;
        const { MEDIA_ERROR } = ErrorEvents;

        if (Check.function(nativePlayer.off) && Check.equal(this.listener, listener)) {

            this.listener = null;

            nativePlayer.off(MEDIA_STARTED, boundHandlers.onPlay);
            nativePlayer.off(MEDIA_RESUMED, boundHandlers.onPlay);
            nativePlayer.off(MEDIA_PAUSED, boundHandlers.onPause);
            nativePlayer.off(MEDIA_ERROR, boundHandlers.onError);
            nativePlayer.off(MEDIA_COMPLETED, boundHandlers.onPlayedToCompletion);

        }

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @param {SDK.Drm.DrmProvider} drmProvider - The DRM provider from the playlist service
     * @returns {Promise<ServiceException>} A promise that rejects because this player adapter requires multiple
     * `SDK.Drm.DrmProvider` instances.
     *
     */
    setDrmProvider(drmProvider) { // eslint-disable-line

        const errorMessage = {
            method: `${this.constructor.name}.setDrmProvider(drmProvider)`,
            get multipleDrmProviders() {
                return `${this.method} - this playerAdapter needs multiple SDK.Drm.DrmProvider instances, please use DssWebPlayerAdapter#setDrmProviders(drmProviders) which takes in an Array of SDK.Drm.DrmProvider instances`; // eslint-disable-line max-len
            }
        };

        const reasons = [new ErrorReason('', errorMessage.multipleDrmProviders)];
        const exceptionData = ExceptionReference.common.invalidState;

        return Promise.reject(new ServiceException({ reasons, exceptionData }));

    }

    /**
     *
     * @access public
     * @param {SDK.Drm.DrmProvider} drmProviders - The array DRM providers from the playlist service
     * @returns {Promise<Void>} A promise that when resolved indicates the DRM providers have been set
     *
     */
    setDrmProviders(drmProviders) {

        const errorMessage = {
            method: `${this.constructor.name}.setDrmProviders(drmProviders)`,
            get drmProviders() {
                return `${this.method} needs Array of SDK.Drm.DrmProvider`;
            }
        };

        array.of.instanceStrict(drmProviders, DrmProvider, errorMessage.drmProviders, InvalidArgumentException);

        const { nativePlayer } = this;

        this.drmProviders = drmProviders;

        nativePlayer.drm.configuration = this.getDrmConfiguration();

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>} - A promise that resolves after the `PlayerAdapter` has been reset.
     * @note Create a new playback session upon onError
     *
     */
    clean() {

        const { listener, nativePlayer, boundHandlers } = this;
        const { PlayerEvents, ErrorEvents } = nativePlayer.events;
        const { MEDIA_COMPLETED, MEDIA_PAUSED, MEDIA_RESUMED, MEDIA_STARTED } = PlayerEvents.PLAYBACK;
        const { MEDIA_ERROR } = ErrorEvents;

        if (Check.function(nativePlayer.off)) {

            nativePlayer.off(MEDIA_STARTED, boundHandlers.onPlay);
            nativePlayer.off(MEDIA_RESUMED, boundHandlers.onPlay);
            nativePlayer.off(MEDIA_PAUSED, boundHandlers.onPause);
            nativePlayer.off(MEDIA_ERROR, boundHandlers.onError);
            nativePlayer.off(MEDIA_COMPLETED, boundHandlers.onPlayedToCompletion);

        }

        this.playlistUri = '';
        this.boundHandlers = {};
        this.drmProviders = null;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc The DRM Configuration contains an array of key system configurations, one for
     * each supported key system. At a minimum, the key system configuration specifies
     * the key system, and a method to acquire a license, either a uri or a callback function.
     *
     * If a callback is provided, then the license request uri is optional, and would be available to the callback.
     * If no callback is provided, then the licenseRequestUri is not optional.
     *
     * @note licenseRequestHeaders expects a name/value set,
     * i.e. [{name: 'Content-Type', value: 'application/octet-stream'}]
     * @note serverCertifcate is mandatory for FAIRPLAY and recommended for WIDEVINE
     * @note serverCertificate is expected to be a Promise<BufferSource> or BufferSource
     * @see https://github.bamtech.co/bam-hls/bam-hls.js/blob/master/API.md#keysystemconfiguration
     * @returns {Object<Array>} The DRM configuration object
     *
     */
    getDrmConfiguration() {

        const { drmProviders } = this;
        const keySystems = [];

        for (const drmProvider of drmProviders) {

            const keySystem = drmProvider.type;
            const licenseRequestUri = Util.getSafe(() => drmProvider.licenseRequestUri);
            const licenseRequestHeaders = drmProvider.processLicenseRequestHeaders();

            keySystems.push({
                keySystem,
                licenseRequestUri,
                licenseRequestHeaders: Object.keys(licenseRequestHeaders).map((item) => ({
                    name: item,
                    value: licenseRequestHeaders[item]
                })),
                /**
                 *
                 * @note bam-hls calls `Promise.resolve(this._keySystemConfig.serverCertificate)`
                 * where serverCertificate is a reference to this function, in EME.js.
                 * We need to use an IIFE here so we setup a promise since they are not executing this function
                 * only expecting a Promise(cert), Promise(undefined), cert or undefined
                 *
                 */
                serverCertificate: (() => {

                    if (keySystem === DrmType.FAIRPLAY) {

                        return drmProvider.getFairPlayCertificate();

                    } else if (keySystem === DrmType.WIDEVINE) {

                        return drmProvider.getWidevineCertificate();

                    }

                    // bam-hls expects undefined so they don't attempt to process this as a certificate
                    return Promise.resolve(undefined);

                })()
            });

        }

        return {
            keySystems
        };

    }

    /**
     *
     * @access public
     * @param {Object} trackingData - The tracking data for the current `playlistUri`
     * @returns {Promise<Void>} A promise that resolves when the tracking data is set
     *
     */
    setTrackingData(trackingData) {

        const errorMessage = {
            method: `${this.constructor.name}.setTrackingData(trackingData)`,
            get trackingData() {
                return `${this.method} needs Object`;
            }
        };

        nonEmptyObject(trackingData, errorMessage.trackingData, InvalidArgumentException);

        return new Promise((resolve) => {

            this.trackingData = trackingData;

            return resolve();

        });

    }

    /**
     *
     * @access private
     * @returns {Object} The adEngine headers
     *
     */
    getAdEngineHeaders() {

        const { trackingData: { adEngine = {} } } = this;
        const { fguid: ssess } = adEngine;

        return {
            ssess
        };

    }

    /**
     *
     * @access public
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>} - A promise that resolves after the PlayerAdapter has been disposed.
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;

        return Promise.resolve();

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/dssWebPlayerAdapter.js